import { Link, createFileRoute } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { formatCount, formatDate, orDash } from "~/lib/format";
import { useStorefronts } from "~/lib/queries";
import { CallbackBadge, credentialLabel } from "./-callback-state";

interface StorefrontSearch {
  search?: string;
  limit?: number;
  offset?: number;
}

export const Route = createFileRoute("/_authed/storefronts/")({
  validateSearch: (raw: Record<string, unknown>): StorefrontSearch => ({
    search: typeof raw.search === "string" && raw.search.trim() !== "" ? raw.search : undefined,
    limit: positive(raw.limit),
    offset: positive(raw.offset),
  }),
  component: StorefrontsPage,
});

function positive(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

const COLUMNS = 6;

/**
 * Storefront is Altura's only tenancy axis, so this list is the index of every
 * merchant in the system.
 *
 * The three derived columns -- transaction count, callback state, credential
 * type -- are what make it worth reading. A table of names says who exists; it
 * takes those three to say who is actually live.
 */
function StorefrontsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = {
    search: search.search,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
  const { data, error, isPending, isFetching, refetch } = useStorefronts(filters);

  function patch(next: Partial<StorefrontSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  const rows = data?.items ?? [];
  const searching = search.search !== undefined;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Storefronts"
        description="Every merchant, and whether they are actually set up."
        actions={
          <Button render={<Link to="/storefronts/new" />}>
            <PlusIcon className="size-4" />
            New storefront
          </Button>
        }
      />

      <DebouncedInput
        value={search.search ?? ""}
        onCommit={(value) => patch({ search: value || undefined })}
        placeholder="Search name or shop domain…"
        className="max-w-sm"
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Shop domain</TableHead>
              <TableHead className="text-right">Transactions</TableHead>
              <TableHead>Callback</TableHead>
              <TableHead>Credentials</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  searching
                    ? "No storefronts match this search."
                    : "No storefronts yet. Create the first one."
                }
                onClear={searching ? () => patch({ search: undefined }) : undefined}
              />
            ) : (
              rows.map((storefront) => (
                <TableRow key={storefront.storefront_id}>
                  <TableCell className="max-w-[16rem] truncate font-medium">
                    <Link
                      to="/storefronts/$storefrontId"
                      params={{ storefrontId: storefront.storefront_id }}
                      className="text-primary hover:underline"
                    >
                      {storefront.storefront_name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {orDash(storefront.shop_domain)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(storefront.transaction_count)}
                  </TableCell>
                  <TableCell>
                    <CallbackBadge storefront={storefront} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {credentialLabel(storefront)}
                  </TableCell>
                  {/* created_at only became real in migration 013; rows older
                      than it were backfilled from their first transaction. */}
                  <TableCell className="text-muted-foreground">
                    {formatDate(storefront.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}

